import { createUseStyles } from 'react-jss';

import { focusRing } from '@/components/HeaderAuthSection/styleTokens';

/**
 * Radius sits on the frame, which clips the track — the slides themselves are square so nothing
 * rounds mid-scroll. Mirrors the miniapp's `MediaCarousel` so the same preset cycles identically
 * on the web and in the app.
 */
const useStyles = createUseStyles(
  {
    frame: {
      position: 'relative',
      width: '100%',
      height: '100%',
      // Fallback for a media row with no usable dimensions, where `Media` passes no inline
      // aspect-ratio. `height: 100%` against an auto-height grid cell resolves to `auto`, and
      // this is then what gives the frame a size instead of collapsing it to nothing. A host
      // with a definite height (the preset hero) still wins on `height`.
      aspectRatio: '1 / 1',
      overflow: 'hidden',
      // A default a host can replace, not a value it has to out-specify. A host
      // that puts its own class on the frame (`/profile` hands it the grid's
      // tile class) would otherwise be declaring `border-radius` against this
      // one at equal specificity, leaving stylesheet order to pick the winner —
      // the tile rendered at 12px or 16px depending on which sheet JSS inserted
      // last. Setting the variable makes both declarations agree instead.
      borderRadius: 'var(--media-carousel-radius, var(--corner-radius-r12))',
      backgroundColor: 'var(--background-colors-base)',
    },
    // Moved by transform so the browser animates it on the compositor; `scrollLeft` would fight
    // the page's own scrolling on touch.
    track: {
      display: 'flex',
      width: '100%',
      height: '100%',
      // No `will-change`: it would pin a compositor layer per carousel for the
      // element's whole life — across a grid of tiles that is real memory.
      transition: 'transform 450ms cubic-bezier(0.22, 1, 0.36, 1)',
    },
    // The slide still changes for anyone who asked for less motion — it cuts instead of sliding.
    trackStatic: { transition: 'none' },
    slide: { flex: '0 0 100%', width: '100%', height: '100%', position: 'relative' },
    media: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
    // Only applied when the frame is a button (`onSlideClick`).
    interactive: {
      padding: 0,
      border: 'none',
      cursor: 'pointer',
      ...focusRing,
    },
  },
  { name: 'media-carousel' },
);

export default useStyles;
